import { createRef, useState } from 'react';
import { paramsToObjects } from '../../components/common/create-params';
import { useChatbots, useCreateChatBot, useUpdateChatBot, Chatbot } from '../../hooks/useChatBot';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import HeadBack from './redirect';
import Header from '../../components/common/global-top-bar';
import { Headeroptions } from './listener-constants';
import { PeerHeaderoptions } from './listener-constants';
import SwitchBar from '../../components/switch-bar';
import React from 'react';

const Peersona = () => {
  const params = paramsToObjects() || {};
  const listenerId = parseInt(params['listenerRoleId']);
  const listenerType = params['listenerType'];
  const listenerName = params['listenerName'];
  const queryClient = useQueryClient();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingBot, setEditingBot] = useState<Chatbot | null>(null);
  const defaultFormData = {
    name: '',
    prompt: '',
    welcome_message: '',
    image_url: '',
    display_name: '',
  };
  const [formData, setFormData] = useState(defaultFormData);
  const [imageBase64, setImageBase64] = useState<string>();
  const [enableSaveOption, setEnableSaveOption] = useState(false);

  const fileInput: React.RefObject<HTMLInputElement> = createRef();

  const { data: chatBotsResponse, isLoading } = useChatbots({ listener_role_ids: listenerId });
  const chatBots = chatBotsResponse?.chatbots || [];

  const createMutation = useCreateChatBot();
  const updateMutation = useUpdateChatBot();

  // TODO: This is repeated in a few spots, refactor to a common function (maybe even improve this it seems meh)
  const onFileChange = (event: React.FormEvent<HTMLInputElement>) => {
    try {
      if (event.currentTarget.files?.[0]) {
        let reader = new FileReader();

        reader.onload = (e: any) => {
          const image = new Image();

          image.onload = () => {
            // @ts-ignore
            const height = image.height;
            // @ts-ignore
            const width = image.width;
            if (height === width) {
              setFormData({ ...formData, image_url: e.target.result });
              setImageBase64(e.target.result);
              setEnableSaveOption(true);
            } else {
              toast.error('Image must be equal height and width.');
              return new Error('Image must be equal height and width.');
            }
          };
          image.src = e.target.result;
        };
        reader.readAsDataURL(event.currentTarget.files[0]);
      }
    } catch (error) {
      console.error('err--', error);
    }
  };

  // TODO: This is repeated in a few spots, refactor to a common function (maybe even improve this it seems meh)
  const handleImageInput = () => {
    fileInput.current?.click();
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.prompt.trim()) {
      toast.error('Name and prompt are required');
      return;
    }

    try {
      await createMutation.mutateAsync({
        listener_role_id: listenerId,
        name: formData.name.trim(),
        prompt: formData.prompt.trim(),
        welcome_message: formData.welcome_message.trim(),
        avatar_file_path: formData.image_url.trim(),
        display_name: formData.display_name.trim(),
      });
      toast.success('Chatbot created successfully!');
      setFormData(defaultFormData);
      setShowCreateForm(false);
      setImageBase64('');
      setEnableSaveOption(false);
      queryClient.invalidateQueries(['chatbots']);
    } catch (error) {
      console.error(error);
      toast.error('Failed to create chatbot');
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBot || !formData.name.trim() || !formData.prompt.trim()) {
      toast.error('Name and prompt are required');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: editingBot.id,
        name: formData.name.trim(),
        prompt: formData.prompt.trim(),
        status: editingBot.status,
        welcome_message: formData.welcome_message.trim(),
        avatar_file_path: formData.image_url.trim(),
        display_name: formData.display_name.trim(),
      });
      toast.success('Chatbot updated successfully!');
      setEditingBot(null);
      setFormData(defaultFormData);
      setImageBase64('');
      setEnableSaveOption(false);
      queryClient.invalidateQueries(['chatbots']);
    } catch (error) {
      toast.error('Failed to update chatbot');
    }
  };

  const handleToggle = async (bot: Chatbot) => {
    const newStatus = bot.status === 'ON' ? 'OFF' : 'ON';

    try {
      await updateMutation.mutateAsync({
        id: bot.id,
        name: bot.name,
        prompt: bot.prompt,
        status: newStatus,
        welcome_message: bot.welcome_message,
        avatar_file_path: bot.image_url,
        display_name: bot.display_name,
      });
      toast.success(`Chatbot ${newStatus === 'ON' ? 'enabled' : 'disabled'}`);
      queryClient.invalidateQueries(['chatbots']);
    } catch (error) {
      toast.error('Failed to toggle chatbot');
    }
  };

  const startEdit = (bot: Chatbot) => {
    setEditingBot(bot);
    setFormData({
      name: bot.name || '',
      prompt: bot.prompt || '',
      welcome_message: bot.welcome_message || '',
      image_url: bot.image_url || '',
      display_name: bot.display_name || '',
    });
    setImageBase64(bot.image_url || '');
    setEnableSaveOption(false);
    setShowCreateForm(false);
  };

  const cancelEdit = () => {
    setEditingBot(null);
    setFormData(defaultFormData);
    setImageBase64('');
    setEnableSaveOption(false);
  };

  const startCreate = () => {
    setShowCreateForm(true);
    setEditingBot(null);
    setFormData(defaultFormData);
    setImageBase64('');
    setEnableSaveOption(false);
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-800"></div>
      </div>
    );
  }

  return (
    <>
      <Header heading={<HeadBack listenerName={listenerName || ''} listenerId={listenerId} />} />
      <SwitchBar heading={listenerType === 'peer' ? PeerHeaderoptions : Headeroptions} position={4} forwardParams />
      <div className="mx-auto max-w-4xl p-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-3xl font-bold">Chatbot Management</h1>
          <button
            onClick={startCreate}
            disabled={showCreateForm || !!editingBot}
            className="bg-blue-primary rounded px-4 py-2 text-white hover:bg-blue-700 disabled:bg-gray-400"
          >
            Add New Chatbot
          </button>
        </div>

        {/* Create Form */}
        {showCreateForm && (
          <div className="mb-6 rounded-lg bg-gray-50 p-6">
            <h2 className="mb-4 text-xl font-semibold">Create New Chatbot</h2>
            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="mb-1 block text-sm font-medium text-gray-700">
                  Name
                </label>
                <p className="mb-2 text-sm text-gray-600">
                  This is the name that will be used internally to identify the chatbot, for versioning etc.
                </p>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full rounded border border-gray-300 p-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter chatbot name"
                  required
                />
              </div>
              <div>
                <label htmlFor="display_name" className="mb-1 block text-sm font-medium text-gray-700">
                  Display Name
                </label>
                <p className="mb-2 text-sm text-gray-600">
                  This is the name that will be displayed to the user. Defaults to the peer's display name if not
                  customized.
                </p>
                <input
                  type="text"
                  id="display_name"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className="w-full rounded border border-gray-300 p-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter chatbot display name"
                />
              </div>
              <div>
                <label htmlFor="prompt" className="mb-1 block text-sm font-medium text-gray-700">
                  Prompt
                </label>
                <p className="mb-2 text-sm text-gray-600">
                  This is the prompt that will be used to generate the chatbot's personality and responses.
                </p>
                <textarea
                  id="prompt"
                  value={formData.prompt}
                  onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                  rows={4}
                  className="w-full rounded border border-gray-300 p-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter chatbot prompt"
                  required
                />
              </div>
              <div>
                <label htmlFor="welcome_message" className="mb-1 block text-sm font-medium text-gray-700">
                  Welcome Message
                </label>
                <p className="mb-2 text-sm text-gray-600">
                  This is the message that will be displayed to the user when they start a chat with the chatbot.
                </p>
                <textarea
                  id="welcome_message"
                  value={formData.welcome_message}
                  onChange={(e) => setFormData({ ...formData, welcome_message: e.target.value })}
                  rows={4}
                  className="w-full rounded border border-gray-300 p-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter welcome message"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Peersona Avatar</label>
                <p className="mb-2 text-sm text-gray-600">
                  This image will be displayed for the chatbot. Must be square (equal height and width). Defaults to
                  peer's profile photo if not customized.
                </p>
                <div className="w-full">
                  <div
                    className={`flex aspect-square h-80 w-80 items-center justify-center rounded-sm bg-gray-200 ${
                      !imageBase64 && 'cursor-pointer'
                    }`}
                    {...(!imageBase64 && { onClick: handleImageInput })}
                    data-testid="peersona-image-upload"
                  >
                    {imageBase64 ? (
                      <img className="h-full w-full rounded-sm object-cover" src={imageBase64} alt="chatbotImage" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <button
                          className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-4xl"
                          onClick={(ev) => {
                            ev.preventDefault();
                          }}
                        >
                          +
                        </button>
                        <input
                          ref={fileInput}
                          id="file-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onClick={() => {
                            if (fileInput.current) {
                              fileInput.current.value = '';
                            }
                          }}
                          onChange={onFileChange}
                        />
                      </div>
                    )}
                  </div>
                  <div className="px-12">
                    {imageBase64 && (
                      <button
                        type="button"
                        className="bg-blue-primary my-2 w-1/3 rounded-full p-2 text-center text-sm text-white"
                        onClick={() => {
                          setImageBase64('');
                          setFormData({ ...formData, image_url: '' });
                          setEnableSaveOption(true);
                        }}
                        data-testid="peersona-remove-image"
                      >
                        REMOVE
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={createMutation.isLoading}
                  className="bg-blue-primary rounded px-4 py-2 text-white hover:bg-green-700 disabled:bg-gray-400"
                >
                  {createMutation.isLoading ? 'Creating...' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="bg-red rounded px-4 py-2 text-white hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Chatbots List */}
        <div className="space-y-4">
          {chatBots.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              No chatbots found. Create your first chatbot to get started.
            </div>
          ) : (
            chatBots.map((bot: Chatbot) => (
              <div key={bot.id} className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                {editingBot?.id === bot.id ? (
                  // Edit Form
                  <form onSubmit={handleEditSubmit} className="space-y-4">
                    <div>
                      <label htmlFor={`name-${bot.id}`} className="mb-1 block text-sm font-medium text-gray-700">
                        Name (internal)
                      </label>
                      <p className="mb-2 text-sm text-gray-600">
                        This is the name that will be used internally to identify the chatbot, for versioning etc.
                      </p>
                      <input
                        type="text"
                        id={`name-${bot.id}`}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full rounded border border-gray-300 p-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`display_name-${bot.id}`}
                        className="mb-1 block text-sm font-medium text-gray-700"
                      >
                        Display Name
                      </label>
                      <p className="mb-2 text-sm text-gray-600">
                        This is the name that will be displayed to the user. Defaults to the peer's display name if not
                        customized.
                      </p>
                      <input
                        type="text"
                        id={`display_name-${bot.id}`}
                        value={formData.display_name}
                        onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                        className="w-full rounded border border-gray-300 p-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor={`prompt-${bot.id}`} className="mb-1 block text-sm font-medium text-gray-700">
                        Prompt
                      </label>
                      <p className="mb-2 text-sm text-gray-600">
                        This is the prompt that will be used to generate the chatbot's personality and responses.
                      </p>
                      <textarea
                        id={`prompt-${bot.id}`}
                        value={formData.prompt}
                        onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                        rows={4}
                        className="w-full rounded border border-gray-300 p-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`welcome_message-${bot.id}`}
                        className="mb-1 block text-sm font-medium text-gray-700"
                      >
                        Welcome Message
                      </label>
                      <p className="mb-2 text-sm text-gray-600">
                        This is the message that will be displayed to the user when they start a chat with the chatbot.
                      </p>
                      <textarea
                        id={`welcome_message-${bot.id}`}
                        value={formData.welcome_message}
                        onChange={(e) => setFormData({ ...formData, welcome_message: e.target.value })}
                        rows={4}
                        className="w-full rounded border border-gray-300 p-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Peersona Avatar</label>
                      <p className="mb-2 text-sm text-gray-600">
                        This image will be displayed for the chatbot. Must be square (equal height and width). Defaults
                        to peer's profile photo if not customized.
                      </p>
                      <div className="w-full">
                        <div
                          className={`flex aspect-square h-80 w-80 items-center justify-center rounded-sm bg-gray-200 ${
                            !imageBase64 && 'cursor-pointer'
                          }`}
                          {...(!imageBase64 && { onClick: handleImageInput })}
                          data-testid="peersona-edit-image-upload"
                        >
                          {imageBase64 ? (
                            <img
                              className="h-full w-full rounded-sm object-cover"
                              src={imageBase64}
                              alt="chatbotImage"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <button
                                className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-4xl"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                }}
                              >
                                +
                              </button>
                              <input
                                ref={fileInput}
                                id="file-upload"
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onClick={() => {
                                  if (fileInput.current) {
                                    fileInput.current.value = '';
                                  }
                                }}
                                onChange={onFileChange}
                              />
                            </div>
                          )}
                        </div>
                        <div className="px-12">
                          {imageBase64 && (
                            <button
                              type="button"
                              className="bg-blue-primary my-2 w-1/3 rounded-full p-2 text-center text-sm text-white"
                              onClick={() => {
                                setImageBase64('');
                                setFormData({ ...formData, image_url: '' });
                                setEnableSaveOption(true);
                              }}
                              data-testid="peersona-edit-remove-image"
                            >
                              REMOVE
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={updateMutation.isLoading}
                        className="bg-blue-primary rounded px-4 py-2 text-white hover:bg-green-700 disabled:bg-gray-400"
                      >
                        {updateMutation.isLoading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="bg-red rounded px-4 py-2 text-white hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  // Display Mode
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="mb-2 flex items-center gap-4">
                        <h3 className="text-lg font-semibold">{bot.name}</h3>
                        <span
                          className={`rounded px-2 py-1 text-xs font-medium ${
                            bot.status === 'ON' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {bot.status}
                        </span>
                      </div>
                      <p className="mb-2 text-sm text-gray-600">
                        {bot.prompt.length > 100 ? `${bot.prompt.substring(0, 100)}...` : bot.prompt}
                      </p>
                      <p className="text-xs text-gray-400">Created: {new Date(bot.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button onClick={() => startEdit(bot)} className="font-medium text-blue-600 hover:text-blue-800">
                        Edit
                      </button>
                      <label htmlFor={`status-${bot.id}`} className="relative inline-flex cursor-pointer items-center">
                        <input
                          id={`status-${bot.id}`}
                          type="checkbox"
                          className="peer sr-only"
                          checked={bot.status === 'ON'}
                          onChange={() => handleToggle(bot)}
                          disabled={updateMutation.isLoading}
                        />
                        <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-900 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-900"></div>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default Peersona;
